import { computed, signal } from '@lit-labs/signals';
import { addDoc, deleteDoc, doc, getDocs, limit, onSnapshot, query, startAfter, updateDoc, } from 'firebase/firestore';
/**
 * Lightweight Firestore helper that manages pagination, CRUD operations, and
 * optional real-time listeners for a single collection. Domain stores compose
 * this class and provide any additional conversion logic.
 */
export class FirestoreCollectionStore {
    collection;
    defaultConstraints;
    transforms;
    mapDocument;
    currentQuery;
    queryDescriptionSignal = signal(null);
    documentsSignal = signal([]);
    statusSignal = signal('idle');
    errorSignal = signal(null);
    lastUpdatedSignal = signal(null);
    pageSizeSignal = signal(10);
    pageIndexSignal = signal(0);
    hasNextSignal = signal(false);
    hasPreviousSignal = signal(false);
    listeningSignal = signal(false);
    unsubscribeRealtime = null;
    pageAnchors = [null];
    pendingRequestId = 0;
    state = computed(() => ({
        status: this.statusSignal.get(),
        documents: this.documentsSignal.get(),
        error: this.errorSignal.get(),
        isListening: this.listeningSignal.get(),
        lastUpdated: this.lastUpdatedSignal.get(),
        currentPage: this.pageIndexSignal.get() + 1,
        pageSize: this.pageSizeSignal.get(),
        hasNextPage: this.hasNextSignal.get(),
        hasPreviousPage: this.hasPreviousSignal.get(),
        queryDescription: this.queryDescriptionSignal.get(),
    }));
    constructor(collection, options = {}) {
        this.collection = collection;
        this.defaultConstraints = options.defaultConstraints ?? [];
        this.transforms = {
            transformCreate: options.transformCreate,
            transformUpdate: options.transformUpdate,
        };
        this.mapDocument = options.mapDocument;
        this.currentQuery = options.initialQuery ?? [];
        this.queryDescriptionSignal.set(options.defaultQueryDescription ?? null);
        this.pageSizeSignal.set(options.pageSize ?? 10);
    }
    async loadInitialPage() {
        this.pageAnchors = [null];
        await this.loadPageAt(0);
    }
    async loadNextPage() {
        if (!this.hasNextSignal.get())
            return;
        await this.loadPageAt(this.pageIndexSignal.get() + 1);
    }
    async loadPreviousPage() {
        if (!this.hasPreviousSignal.get())
            return;
        await this.loadPageAt(this.pageIndexSignal.get() - 1);
    }
    async refresh() {
        await this.loadPageAt(this.pageIndexSignal.get());
    }
    async setQuery(constraints, description = null) {
        this.currentQuery = constraints;
        this.queryDescriptionSignal.set(description);
        this.stopRealtime();
        await this.loadInitialPage();
    }
    async setPageSize(size) {
        if (size <= 0)
            throw new Error('Page size must be positive');
        if (this.pageSizeSignal.get() === size)
            return;
        this.pageSizeSignal.set(size);
        await this.loadInitialPage();
    }
    startRealtime() {
        this.stopRealtime();
        const builtQuery = this.buildQuery(this.pageIndexSignal.get());
        this.statusSignal.set('loading');
        this.errorSignal.set(null);
        this.unsubscribeRealtime = onSnapshot(builtQuery, (snapshot) => {
            const prepared = this.processSnapshot(snapshot.docs);
            this.documentsSignal.set(prepared.documents);
            this.hasNextSignal.set(prepared.hasNextPage);
            this.hasPreviousSignal.set(this.pageIndexSignal.get() > 0);
            this.lastUpdatedSignal.set(Date.now());
            this.statusSignal.set('ready');
            if (prepared.anchor) {
                this.pageAnchors[this.pageIndexSignal.get() + 1] = prepared.anchor;
            }
            else {
                this.pageAnchors = this.pageAnchors.slice(0, this.pageIndexSignal.get() + 1);
            }
        }, (error) => {
            this.statusSignal.set('error');
            this.errorSignal.set(error.message ?? 'Failed to listen for updates');
        });
        this.listeningSignal.set(true);
    }
    stopRealtime() {
        if (this.unsubscribeRealtime) {
            this.unsubscribeRealtime();
            this.unsubscribeRealtime = null;
        }
        this.listeningSignal.set(false);
    }
    async create(data) {
        const basePayload = { ...data };
        const payload = this.transforms.transformCreate ? this.transforms.transformCreate(basePayload) : basePayload;
        const docRef = await addDoc(this.collection, payload);
        return docRef.id;
    }
    async update(id, data) {
        const basePayload = { ...data };
        const payload = this.transforms.transformUpdate ? this.transforms.transformUpdate(basePayload) : basePayload;
        if (!Object.keys(payload).length)
            return;
        await updateDoc(doc(this.collection, id), payload);
    }
    async delete(id) {
        await deleteDoc(doc(this.collection, id));
    }
    async loadPageAt(targetIndex) {
        this.pendingRequestId += 1;
        const requestId = this.pendingRequestId;
        this.stopRealtime();
        this.statusSignal.set('loading');
        this.errorSignal.set(null);
        try {
            const builtQuery = this.buildQuery(targetIndex);
            const snapshot = await getDocs(builtQuery);
            if (requestId !== this.pendingRequestId)
                return;
            const prepared = this.processSnapshot(snapshot.docs);
            this.documentsSignal.set(prepared.documents);
            this.lastUpdatedSignal.set(Date.now());
            this.hasNextSignal.set(prepared.hasNextPage);
            this.pageIndexSignal.set(targetIndex);
            this.hasPreviousSignal.set(targetIndex > 0);
            if (prepared.anchor) {
                this.pageAnchors[targetIndex + 1] = prepared.anchor;
            }
            else {
                this.pageAnchors = this.pageAnchors.slice(0, targetIndex + 1);
            }
            this.statusSignal.set('ready');
        }
        catch (error) {
            if (requestId !== this.pendingRequestId)
                return;
            this.statusSignal.set('error');
            this.errorSignal.set(error instanceof Error ? error.message : 'Failed to load documents');
        }
    }
    buildQuery(pageIndex) {
        const constraints = [...this.defaultConstraints, ...this.currentQuery];
        const pageSize = this.pageSizeSignal.get();
        const anchor = this.pageAnchors[pageIndex];
        if (anchor)
            constraints.push(startAfter(anchor));
        constraints.push(limit(pageSize + 1));
        return query(this.collection, ...constraints);
    }
    processSnapshot(docs) {
        const pageSize = this.pageSizeSignal.get();
        const hasNextPage = docs.length > pageSize;
        const trimmedDocs = hasNextPage ? docs.slice(0, pageSize) : docs;
        const documents = trimmedDocs.map((docSnap) => this.hydrateDocument(docSnap));
        const anchor = trimmedDocs.length ? trimmedDocs[trimmedDocs.length - 1] : null;
        return { documents, hasNextPage, anchor };
    }
    hydrateDocument(snapshot) {
        const raw = snapshot.data();
        const baseDoc = { id: snapshot.id, ...raw };
        return this.mapDocument ? this.mapDocument(baseDoc) : baseDoc;
    }
}
//# sourceMappingURL=firestore-base.store.js.map